import { useState } from 'react';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import { CurrencySelectorComponent } from '../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import { initialsOf } from './General/Cashflow.Dashboard.Functions';
import { MEDICAL_CENTER_NAME } from './General/Cashflow.General.Functions';
import { parseAmount } from './CashFlow.Form.Logic';
import './CashFlow.Form.Styles.css';

/** Piezas comunes a los formularios de deuda y pago. */

export const Step = ({ n, title, hint, error, children }: {
    n: number;
    title: string;
    hint?: React.ReactNode;
    error?: string;
    children: React.ReactNode;
}) => (
    <section className={`cff-step${error ? ' cff-step--error' : ''}`}>
        <div className="cff-step__head">
            <span className="cff-step__n" aria-hidden>{n}</span>
            <h4 className="cff-step__title">{title}</h4>
        </div>
        {hint && <div className="cff-step__hint">{hint}</div>}
        {children}
        {error && <div className="cff-error" role="alert">{error}</div>}
    </section>
);

export const FieldLabel = ({ text, required, help }: { text: string; required?: boolean; help?: string }) => (
    <>
        <span className="cff-label">{text}{required && <span className="req" aria-hidden>*</span>}</span>
        {help && <span className="cff-help">{help}</span>}
    </>
);

/** Una parte que ya se conoce: se muestra, no se pide. */
export const PartyChip = ({ role, name }: { role: string; name: string }) => (
    <span className="cff-party" title={name}>
        <span className={`cff-party__avatar${name === MEDICAL_CENTER_NAME ? ' cff-party__avatar--me' : ''}`} aria-hidden>{initialsOf(name)}</span>
        <span style={{ minWidth: 0 }}>
            <span className="cff-party__role">{role}</span>
            <span className="cff-party__name">{name}</span>
        </span>
    </span>
);

/** Chips con las monedas probables y "Otra…" para el resto. */
export const CurrencyChips = ({ suggested, value, onChange, disabled }: {
    suggested: string[];
    value?: string;
    onChange: (currency: string) => void;
    disabled?: boolean;
}) => {
    const [other, setOther] = useState(false);
    const chips = value && !suggested.includes(value) ? [...suggested, value] : suggested;

    if (disabled) return <span className="cff-chips"><span className="cff-chip is-active">{value}</span></span>;

    return (
        <div className="cff-chips">
            {chips.map((code) => (
                <button key={code} type="button" className={`cff-chip${value === code ? ' is-active' : ''}`} onClick={() => { setOther(false); onChange(code); }}>
                    {code}
                </button>
            ))}
            {!other && <button type="button" className="cff-chip cff-chip--ghost" onClick={() => setOther(true)}>Otra…</button>}
            {other && (
                <div style={{ flexBasis: '100%' }}>
                    <CurrencySelectorComponent
                        errorMessage={undefined}
                        defaultSelected={undefined}
                        label={undefined}
                        onCurrencyChange={(code) => code && onChange(code)}
                    />
                </div>
            )}
        </div>
    );
};

/** Importe grande, con la moneda al lado. Acepta coma o punto decimal. */
export const AmountInput = ({ value, currency, onChange, error, autoFocus, id }: {
    value: string;
    currency?: string;
    onChange: (text: string, amount: number) => void;
    error?: boolean;
    autoFocus?: boolean;
    id?: string;
}) => (
    <label className={`cff-amount${error ? ' cff-amount--error' : ''}`} htmlFor={id}>
        <span className="cff-amount__cur">{currency || '—'}</span>
        <input
            id={id}
            inputMode="decimal"
            placeholder="0,00"
            value={value}
            autoFocus={autoFocus}
            onChange={(event) => onChange(event.target.value, parseAmount(event.target.value))}
        />
    </label>
);

/** Sección plegada con lo que casi nunca hace falta. */
export const MoreData = ({ label = 'Más datos (opcional)', open: openProp, children }: {
    label?: string;
    open?: boolean;
    children: React.ReactNode;
}) => {
    const [open, setOpen] = useState(!!openProp);
    return (
        <>
            <button type="button" className="cff-more" onClick={() => setOpen((current) => !current)} aria-expanded={open}>
                {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />} {label}
            </button>
            {open && children}
        </>
    );
};
